/**
 * AuthContext - Azure Static Web Apps Authentication
 * 
 * Manages authentication state using Azure SWA's built-in auth with Entra ID.
 * Uses /.auth/me endpoint to get user info and /.auth/login/aad for login.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

/**
 * Custom hook to use auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Parse the client principal from Azure SWA
 */
const parseClientPrincipal = (clientPrincipal) => {
  if (!clientPrincipal) return null;
  
  return {
    userId: clientPrincipal.userId,
    userDetails: clientPrincipal.userDetails, // Usually the email
    identityProvider: clientPrincipal.identityProvider,
    userRoles: clientPrincipal.userRoles || [],
    claims: clientPrincipal.claims || [],
  };
};

/**
 * AuthProvider Component
 * Wraps the app and provides authentication state
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch user info from Azure SWA /.auth/me endpoint
   */
  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/.auth/me');
      
      if (!response.ok) {
        // User is not authenticated
        setUser(null);
        return null;
      }
      
      const data = await response.json();
      
      if (data.clientPrincipal) {
        const parsedUser = parseClientPrincipal(data.clientPrincipal);
        // Cache the raw principal for backend calls that accept SWA headers
        try {
          localStorage.setItem('swaPrincipal', btoa(JSON.stringify(data.clientPrincipal)));
        } catch (e) {
          console.warn('Unable to cache SWA principal', e);
        }
        setUser(parsedUser);
        return parsedUser;
      } else {
        localStorage.removeItem('swaPrincipal');
        setUser(null);
        return null;
      }
    } catch (err) {
      console.error('Error fetching auth info:', err);
      setError('Failed to check authentication status');
      localStorage.removeItem('swaPrincipal');
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check authentication on mount
   */
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  /**
   * Login via Entra ID (Azure AD)
   * Redirects to Azure login page
   */
  const login = useCallback((returnUrl = '/admin') => {
    // Encode the return URL
    const encodedReturn = encodeURIComponent(returnUrl);
    window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodedReturn}`;
  }, []);

  /**
   * Logout and clear session
   */
  const logout = useCallback(async (returnUrl = '/admin/login') => {
    const encodedReturn = encodeURIComponent(returnUrl);
    window.location.href = `/.auth/logout?post_logout_redirect_uri=${encodedReturn}`;
  }, []);

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback((role) => {
    if (!user || !user.userRoles) return false;
    return user.userRoles.includes(role);
  }, [user]);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = !!user;

  /**
   * Check if user is admin (has 'admin' or 'authenticated' role)
   */
  const isAdmin = isAuthenticated && hasRole('admin');

  /**
   * Get user's display name (email or user details)
   */
  const displayName = user?.userDetails || user?.userId || 'User';

  /**
   * Get user's email from claims if available
   */
  const email = user?.claims?.find(c => c.typ === 'preferred_username')?.val || 
                user?.claims?.find(c => c.typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress')?.val ||
                user?.userDetails || '';

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    displayName,
    email,
    login,
    logout,
    hasRole,
    refreshUser: fetchUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
